import { get, isEmpty, last, sortBy } from "lodash";
import { KEYS, SUBSCRIPTION_STATUSES, TEMPLATE_TYPES } from "utils/constants";

const SIGNED_RESUME_TEMPLATES = ["SUBSCRIPTION_DONE", "B2C_SUBSCRIPTION_DONE"];

const CLOSED_SUBSCRIPTION_STATUSES = [
  SUBSCRIPTION_STATUSES.SIGNED,
  SUBSCRIPTION_STATUSES.COMPLETED,
];

export const isSignedSubscription = (subscription) =>
  Boolean(
    subscription &&
    CLOSED_SUBSCRIPTION_STATUSES.includes(get(subscription, "status")),
  );

export const resolveSignedResumeStatus = (process) => {
  const catalog = get(process, "processStatuses") || [];
  return (
    catalog.find((status) =>
      SIGNED_RESUME_TEMPLATES.includes(get(status, "template")),
    ) || null
  );
};

export const isDynamicProcessTheme = (theme) =>
  theme === TEMPLATE_TYPES.B2B_DYNAMIC || theme === TEMPLATE_TYPES.B2C_DYNAMIC;

export const isAcorisProcessTheme = (theme) =>
  theme === TEMPLATE_TYPES.B2B_ACORIS || theme === TEMPLATE_TYPES.B2C_ACORIS;

export const resolveProcessFromProject = (project) => {
  const rows = get(project, "statuses") || [];
  for (const row of rows) {
    const process = get(row, "status.process");
    if (get(process, "id")) return process;
  }
  return null;
};

export const resolveProcess = ({ locationProcess, storedProcess, project }) => {
  if (get(locationProcess, "id")) return locationProcess;
  if (get(storedProcess, "id")) return storedProcess;
  return resolveProcessFromProject(project);
};

export const resolveRuntimeTracer = ({
  locationTracer,
  storedTracer,
  project,
}) => {
  if (get(locationTracer, "id")) return locationTracer;
  const projectTracer = get(project, "tracer") || null;
  if (get(storedTracer, "id")) {
    if (get(storedTracer, "type")) return storedTracer;
    if (get(projectTracer, "type") || get(projectTracer, "flow")) {
      return {
        ...projectTracer,
        ...storedTracer,
        type: get(storedTracer, "type") || get(projectTracer, "type"),
        flow: get(storedTracer, "flow") || get(projectTracer, "flow") || null,
      };
    }
    return storedTracer;
  }
  return projectTracer;
};

const catalogStatusById = (catalog, statusId) =>
  (catalog || []).find((status) => status.id === statusId);

const pickStatusList = (primary, secondary) =>
  Array.isArray(primary) && primary.length ? primary : secondary;

export const mergeProcessStatus = (primary, secondary) => {
  if (!primary && !secondary) return null;
  if (!primary) return secondary;
  if (!secondary) return primary;
  return {
    ...secondary,
    ...primary,
    possibleStatuses: pickStatusList(
      get(primary, "possibleStatuses"),
      get(secondary, "possibleStatuses"),
    ),
    actions: pickStatusList(get(primary, "actions"), get(secondary, "actions")),
  };
};

const statusOrder = (status) => parseInt(get(status, "order"), 10);

export const resolveResumeStatus = ({
  process,
  projectStatuses,
  storedStatusId,
  locationStatus,
  preferLocationStatus,
}) => {
  const catalog = get(process, "processStatuses") || [];
  const rawStatusById = (statusId) =>
    get(
      (projectStatuses || []).find(
        ({ status }) => get(status, "id") === statusId,
      ),
      "status",
    );
  if (preferLocationStatus && get(locationStatus, "id")) {
    return (
      mergeProcessStatus(
        catalogStatusById(catalog, locationStatus.id),
        locationStatus,
      ) || locationStatus
    );
  }
  const stored = mergeProcessStatus(
    catalogStatusById(catalog, storedStatusId),
    rawStatusById(storedStatusId),
  );
  const matching = sortBy(
    (projectStatuses || []).filter(
      ({ status }) => get(status, "process.id") === get(process, "id"),
    ),
    "createdDate",
  );
  const lastReachedRaw = get(last(matching), "status");
  const lastReached = mergeProcessStatus(
    catalogStatusById(catalog, get(lastReachedRaw, "id")),
    lastReachedRaw,
  );

  const firstStatus = sortBy(catalog, statusOrder)[0];
  const storedIsFirstStep =
    stored && firstStatus && stored.id === firstStatus.id;
  const reachedOrder = statusOrder(lastReached);
  const firstOrder = statusOrder(firstStatus);
  if (
    !isDynamicProcessTheme(get(process, "theme")) &&
    storedIsFirstStep &&
    lastReached &&
    !isNaN(reachedOrder) &&
    !isNaN(firstOrder) &&
    reachedOrder > firstOrder
  ) {
    return lastReached;
  }

  if (stored) return stored;
  if (lastReached) return lastReached;
  if (get(locationStatus, "id")) {
    return catalogStatusById(catalog, locationStatus.id) || locationStatus;
  }
  return null;
};

export const persistStaticResumeStatus = (theme, statusId) => {
  if (!statusId || isDynamicProcessTheme(theme)) return;
  localStorage.setItem(KEYS.STATUS_ID, statusId);
  try {
    const raw = localStorage.getItem(KEYS.PROCESS_RUNTIME);
    if (isEmpty(raw)) return;
    const stored = JSON.parse(raw);
    if (!get(stored, "projectId") || !get(stored, "process.id")) return;
    persistProcessRuntime(
      stored.projectId,
      stored.process,
      stored.tracer,
      statusId,
    );
  } catch (_error) {
    return;
  }
};

const omitStatusScreens = (status) => {
  if (!status || typeof status !== "object") return status;
  const { screens: _screens, ...rest } = status;
  return rest;
};

const slimProcessForStorage = (process) => {
  if (!process || typeof process !== "object") return process;
  if (!Array.isArray(process.processStatuses)) return process;
  return {
    ...process,
    processStatuses: process.processStatuses.map(omitStatusScreens),
  };
};

const slimTracerForStorage = (tracer) => {
  if (!tracer || typeof tracer !== "object") return tracer;
  const nestedProcess = get(tracer, "flow.process");
  if (!nestedProcess) return tracer;
  return {
    ...tracer,
    flow: {
      ...tracer.flow,
      process: slimProcessForStorage(nestedProcess),
    },
  };
};

export const persistProcessRuntime = (projectId, process, tracer, statusId) => {
  if (!projectId || !get(process, "id")) return;
  const previous = readStoredProcessRuntime(projectId);
  const nextStatusId =
    statusId !== undefined && statusId !== null
      ? statusId
      : get(previous, "statusId");
  try {
    localStorage.setItem(
      KEYS.PROCESS_RUNTIME,
      JSON.stringify({
        projectId,
        process: slimProcessForStorage(process),
        tracer: slimTracerForStorage(tracer || get(previous, "tracer") || null),
        ...(nextStatusId ? { statusId: nextStatusId } : {}),
      }),
    );
  } catch (_error) {
    return;
  }
};

export const persistProcessStatus = (projectId, statusId) => {
  if (!statusId) return;
  localStorage.setItem(KEYS.STATUS_ID, statusId);
  if (!projectId) return;
  try {
    const raw = localStorage.getItem(KEYS.PROCESS_RUNTIME);
    if (isEmpty(raw)) return;
    const stored = JSON.parse(raw);
    if (get(stored, "projectId") !== projectId) return;
    if (!get(stored, "process.id")) return;
    persistProcessRuntime(projectId, stored.process, stored.tracer, statusId);
  } catch (_error) {
    return;
  }
};

export const readStoredProcessRuntime = (projectId) => {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(KEYS.PROCESS_RUNTIME);
    if (isEmpty(raw)) return null;
    const stored = JSON.parse(raw);
    if (get(stored, "projectId") !== projectId) return null;
    if (!get(stored, "process.id")) return null;
    return {
      process: stored.process,
      tracer: stored.tracer || null,
      statusId: stored.statusId || null,
    };
  } catch (_error) {
    return null;
  }
};

export const readStoredStatusId = (projectId) =>
  get(readStoredProcessRuntime(projectId), "statusId") || null;

// The subscription id is stored under a single key shared by every dossier, so a
// project without its own subscription would otherwise adopt the one left by the
// dossier the advisor looked at just before. `fields.currentSubscription` gets
// the same treatment because projects created before that leak was fixed still
// carry a foreign id.
export const resolveProjectSubscriptionId = ({
  locationSubscriptionId,
  project,
  storedSubscriptionId,
}) => {
  if (locationSubscriptionId) return locationSubscriptionId;
  const projectSubscriptions = get(project, "subscriptions");
  const projectSubscriptionId = get(project, "fields.currentSubscription");
  if (!Array.isArray(projectSubscriptions)) {
    return projectSubscriptionId || storedSubscriptionId || null;
  }
  const belongsToProject = (subscriptionId) =>
    Boolean(subscriptionId) &&
    projectSubscriptions.some(({ id }) => id === subscriptionId);
  if (belongsToProject(projectSubscriptionId)) return projectSubscriptionId;
  if (belongsToProject(storedSubscriptionId)) return storedSubscriptionId;
  return null;
};

// A brand new project must start from the same blank slate as a fresh sign-in:
// every funnel key below is read back by screens that cannot tell which dossier
// wrote it.
export const clearProjectRuntime = () => {
  [
    KEYS.PROCESS_RUNTIME,
    KEYS.STATUS_ID,
    KEYS.SUBSCRIPTION_ID,
    KEYS.LINK_ID,
  ].forEach((key) => localStorage.removeItem(key));
  localStorage.setItem(KEYS.TEMPLATE_HISTORY, "{}");
};

export const resolveScopedRuntime = ({ projectId, locationState }) => {
  const stored = readStoredProcessRuntime(projectId);
  return {
    process: get(locationState, "process") || get(stored, "process") || null,
    tracer: get(locationState, "tracer") || get(stored, "tracer") || null,
    status: get(locationState, "status") || null,
  };
};

export const hasLocationStatus = (locationState) =>
  !isEmpty(get(locationState, "status"));
